import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Heart, Briefcase, Coins, Home, Activity,
} from 'lucide-react';
import { SectionTitle, FlowerMotif } from './premium/Decor';

/**
 * "What's Inside The Kundli Report?" — mirrors the reference site's
 * 5 hero report features with a left-image + right-list layout.
 * Replaces the prior generic FeaturesSection.
 */
const FEATURES = [
  {
    icon: Heart,
    hi: 'रिश्ते और प्रेम जीवन विश्लेषण',
    en: 'Relationship and Love Life Analysis',
    dHi: 'अपने भावनात्मक pattern, compatibility और रिश्तों को आकार देने वाली बातों को समझें। साथ ही देरी, दूसरे मौके और आदर्श साथी के गुण भी जानें।',
    dEn: 'Understand your emotional patterns, compatibility, and what shapes your relationships. Also uncover delays, second chances, and ideal partner traits.',
  },
  {
    icon: Briefcase,
    hi: 'करियर में वृद्धि की संभावनाएँ',
    en: 'Growth in Career Prospects',
    dHi: 'स्पष्ट मार्गदर्शन कि नौकरी, व्यवसाय या स्वतंत्र पथ आपके लिए बेहतर है। सरकारी नौकरी और competitive exam में सफलता की क्षमता भी जानें।',
    dEn: 'Get clear guidance on whether a job, business, or independent path suits you better — also decode success potential in government roles and competitive exams.',
  },
  {
    icon: Coins,
    hi: 'आर्थिक स्थिरता की अंतर्दृष्टि',
    en: 'Financial Stability Insights',
    dHi: 'धन-संपत्ति, धन प्रवाह, financial blocks और अपनी विस्तृत कुंडली रिपोर्ट में छिपे luck triggers की पहचान।',
    dEn: 'Money & finances, wealth flow, financial blocks, and hidden luck triggers in your detailed Kundli report.',
  },
  {
    icon: Home,
    hi: 'धन और जीवनशैली विस्तार',
    en: 'Wealth & Lifestyle Expansion',
    dHi: 'समझें कि कब आप संपत्ति, वाहन हासिल करेंगे और एक अधिक आरामदायक, समृद्ध जीवनशैली का आनंद लेंगे।',
    dEn: 'Understand when you are likely to acquire property, vehicles, and enjoy a more comfortable, luxurious lifestyle.',
  },
  {
    icon: Activity,
    hi: 'स्वास्थ्य और कल्याण की जानकारी',
    en: 'Health & Well-Being Insights',
    dHi: 'अपने शारीरिक और मानसिक स्वास्थ्य के pattern को समझें और संतुलन कैसे बनाए रखें यह जानें।',
    dEn: 'Understand your physical and mental health patterns and how to maintain balance.',
  },
];

const FeaturesSection = () => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="features-section"
      className="relative bg-brand-cream py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'रिपोर्ट के अंदर' : 'Inside the Report'}
          title={isHi ? 'कुंडली रिपोर्ट में क्या है?' : "WHAT'S INSIDE THE KUNDLI REPORT?"}
          subtitle={isHi
            ? '60+ पेज की विस्तृत वैदिक कुंडली रिपोर्ट — आपके जीवन के हर पहलू पर गहन विश्लेषण।'
            : '60+ pages of deep Vedic analysis — covering every dimension of your life.'}
          language={language}
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT — vertical features list */}
          <div className="lg:col-span-7 space-y-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="group flex gap-5 bg-white rounded-2xl border border-brand-gold/25 p-5 md:p-6 hover:border-brand-gold hover:shadow-gold-glow hover:-translate-y-0.5 transition-all duration-300 opacity-0 animate-fade-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-brand-gold-light to-brand-gold/40 flex items-center justify-center group-hover:from-brand-maroon group-hover:to-brand-maroon-dark transition-all">
                      <Icon className="w-7 h-7 text-brand-maroon group-hover:text-brand-gold-light transition-colors" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h3 className={`font-playfair font-bold text-lg md:text-xl text-brand-maroon mb-1.5 ${isHi ? 'font-hindi-serif' : ''}`}>
                      {isHi ? f.hi : f.en}
                    </h3>
                    <p className={`text-brand-text-soft text-sm md:text-base leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? f.dHi : f.dEn}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — decorative report image */}
          <div className="lg:col-span-5 relative order-first lg:order-last">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-6 bg-brand-gold/20 blur-3xl rounded-full" />

              <div className="relative aspect-[3/4] rounded-3xl bg-gradient-to-br from-brand-cream via-white to-brand-gold-light/40 border-4 border-brand-gold shadow-premium overflow-hidden">
                {/* Mandala backdrop */}
                <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-25" />

                {/* Open report pages illustration */}
                <div className="relative h-full p-6 flex flex-col">
                  {/* Header */}
                  <div className="text-center pb-4 border-b border-brand-gold/30">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold-dark font-semibold">
                      {isHi ? 'जन्मकुंडली रिपोर्ट' : 'Kundli Report'}
                    </p>
                    <h4 className={`font-playfair font-bold text-2xl text-brand-maroon mt-1 ${isHi ? 'font-hindi-serif' : ''}`}>
                      {isHi ? 'भाव विश्लेषण' : 'House Analysis'}
                    </h4>
                  </div>

                  {/* Mock chart */}
                  <div className="flex-1 flex items-center justify-center my-3">
                    <svg viewBox="0 0 200 200" className="w-full h-auto max-w-[220px]">
                      <rect x="10" y="10" width="180" height="180" fill="none" stroke="#73111E" strokeWidth="2" />
                      <line x1="10" y1="10" x2="190" y2="190" stroke="#73111E" strokeWidth="1.2" />
                      <line x1="190" y1="10" x2="10" y2="190" stroke="#73111E" strokeWidth="1.2" />
                      <line x1="100" y1="10" x2="10" y2="100" stroke="#73111E" strokeWidth="1.2" />
                      <line x1="10" y1="100" x2="100" y2="190" stroke="#73111E" strokeWidth="1.2" />
                      <line x1="100" y1="190" x2="190" y2="100" stroke="#73111E" strokeWidth="1.2" />
                      <line x1="190" y1="100" x2="100" y2="10" stroke="#73111E" strokeWidth="1.2" />
                      {[
                        [55,40,'1'],[100,30,'2'],[145,40,'3'],
                        [160,80,'4'],[160,120,'5'],[145,160,'6'],
                        [100,170,'7'],[55,160,'8'],[40,120,'9'],
                        [40,80,'10'],[80,80,'11'],[120,80,'12'],
                      ].map(([x,y,t], i)=> (
                        <text key={i} x={x} y={y} fontSize="9" fill="#73111E" textAnchor="middle" fontFamily="serif">{t}</text>
                      ))}
                    </svg>
                  </div>

                  {/* Bottom strip */}
                  <div className="flex items-center gap-2 justify-center pt-4 border-t border-brand-gold/30">
                    <FlowerMotif className="w-4 h-4 text-brand-gold-dark" />
                    <span className={`text-[11px] uppercase tracking-[0.2em] text-brand-gold-dark font-semibold ${isHi ? 'font-hindi' : ''}`}>
                      Karma Kundali
                    </span>
                    <FlowerMotif className="w-4 h-4 text-brand-gold-dark" />
                  </div>
                </div>
              </div>

              {/* Decorative side ribbon */}
              <div className="absolute -bottom-4 -right-4 bg-brand-maroon text-brand-gold-light rounded-full px-5 py-2.5 shadow-premium border-2 border-brand-gold rotate-3">
                <span className={`font-playfair font-bold text-base ${isHi ? 'font-hindi-serif' : ''}`}>
                  {isHi ? '60+ पेज' : '60+ Pages'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
